import React, { useEffect, useContext } from 'react'
import { Form, Input } from 'antd'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useHistory, useLocation } from 'react-router'

import { ReactComponent as MainEmailIcon } from 'assets/svg/login/main_email.svg'
import { ReactComponent as MainPasswordIcon } from 'assets/svg/login/main_password.svg'
import { ReactComponent as MainLogin } from 'assets/svg/login/main_login.svg'
import { sendResetLink, resetPassword } from 'store/actions/profile'
import { AppUrls } from 'common/AppUrls'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppContext } from 'common/Globals'

export function FormComponent() {
    const { t } = useTranslation()
    const location = useLocation()
    const params = new URLSearchParams(location.search)
    const host = params.get('host')
    const token = params.get('token')
    const isResetPassword = location.pathname === AppUrls.resetPassword
    const history = useHistory()
    const dispatch = useDispatch()
    const { isUserAuthed } = useContext(AppContext)

    useEffect(() => {
        if (isUserAuthed) {
            setTimeout(() => {
                history.push(AppUrls.dialpad)
            }, 1000)
        }
    }, [isUserAuthed, history])

    const initialValues = isResetPassword
        ? {
              password: null,
              password_confirm: null,
              host,
              token
          }
        : {
              email: null
          }

    const validationSchema = isResetPassword
        ? yup.object().shape({
              password: yup
                  .string()
                  .nullable()
                  .required(t('field_required'))
                  .matches(/^\S*(?=\S*[a-z])\S*$/, t('password_validation.lowercase'))
                  .matches(/^\S*(?=\S*[A-Z])\S*$/, t('password_validation.uppercase'))
                  .matches(/^\S*(?=\S*[\d])\S*$/, t('password_validation.digit'))
                  .matches(/^.{8,}$/, t('password_validation.length')),
              password_confirm: yup
                  .string()
                  .nullable()
                  .required(t('field_required'))
                  .oneOf([yup.ref('password'), null], t('password_validation.confirmation'))
          })
        : yup.object().shape({
              email: yup.string().nullable().email(t('email_incorrect')).required(t('field_required'))
          })

    function onSubmit(values) {
        isResetPassword ? dispatch(resetPassword({ values })) : dispatch(sendResetLink({ values }))
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => (
                <div className="password-recovery__form">
                    {isResetPassword ? (
                        <>
                            <p className="password-recovery__form__title">{t('change_password')}</p>
                            <p className="password-recovery__form__label">
                                <strong>{t('password_must')}</strong> {t('password_recovery_strength')}
                            </p>
                            <Form.Item
                                className="password-recovery__form-item"
                                validateStatus={errors.password ? 'error' : null}
                            >
                                <Input
                                    name="password"
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="app__input"
                                    placeholder={t('new_pass_ph')}
                                    type="password"
                                    prefix={<MainPasswordIcon />}
                                />
                                {errors.password && touched.password && (
                                    <p className="password-recovery__form-error">{errors.password}</p>
                                )}
                            </Form.Item>
                            <Form.Item
                                className="password-recovery__form-item"
                                validateStatus={errors.password_confirm ? 'error' : null}
                            >
                                <Input
                                    name="password_confirm"
                                    value={values.password_confirm}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="app__input"
                                    placeholder={t('confirm_pass_ph')}
                                    type="password"
                                    prefix={<MainPasswordIcon />}
                                />
                                {errors.password_confirm && touched.password_confirm && (
                                    <p className="password-recovery__form-error">{errors.password_confirm}</p>
                                )}
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <p className="password-recovery__form__label">{t('forgot_password')}</p>
                            <Form.Item
                                className="password-recovery__form-item"
                                validateStatus={errors.email ? 'error' : null}
                            >
                                <Input
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="app__input"
                                    placeholder={t('email')}
                                    prefix={<MainEmailIcon />}
                                />
                                {errors.email && touched.email && (
                                    <p className="password-recovery__form-error">{errors.email}</p>
                                )}
                            </Form.Item>
                        </>
                    )}
                    <button type="button" onClick={() => handleSubmit()} className="password-recovery__submit">
                        <MainLogin width={15} height={15} />
                        {t('reset_password')}
                    </button>
                    <div className="d-flex justify-content-center">
                        <NavLink to={AppUrls.login} className="password-recovery__back-link">
                            {t('cancel')}
                        </NavLink>
                    </div>
                </div>
            )}
        </Formik>
    )
}
